import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, ArrowLeft, Pencil, MessageCircle, UserPlus } from "lucide-react";
import SpotifyEmbed from "@/components/SpotifyEmbed";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import ProfileConnect from "@/components/ProfileConnect";
import EditProfileModal from "@/components/EditProfileModal";
import ProfileExtras from "@/components/ProfileExtras";
import StreamingLinks from "@/components/StreamingLinks";

export default function ProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [exclusiveLock, setExclusiveLock] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get(`/profiles/${id}`)
      .then((r) => setProfile(r.data))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 402 && detail?.exclusive) {
          setExclusiveLock(detail);
        } else {
          setError("Profile not found");
        }
      });
  }, [id]);

  if (exclusiveLock) {
    return (
      <div className="min-h-[80vh] bg-ink-900 flex items-center justify-center px-5 py-20" data-testid="exclusive-paywall">
        <div className="max-w-md text-center">
          {exclusiveLock.avatar && (
            <img src={exclusiveLock.avatar} alt={exclusiveLock.name} className="w-24 h-24 mx-auto object-cover border border-pfc-gold/40 mb-6" />
          )}
          <div className="text-pfc-gold text-[11px] tracking-[0.4em] uppercase font-sub mb-3">Exclusive Member Profile</div>
          <h1 className="font-display text-3xl text-white mb-3">{exclusiveLock.name}</h1>
          {exclusiveLock.headline && <p className="text-white/60 mb-6">{exclusiveLock.headline}</p>}
          <p className="text-white/70 mb-8 text-sm">
            This profile is reserved for PFC Premium and PFC Community subscribers. Unlock {exclusiveLock.name}'s exclusive content, behind-the-scenes drops, and direct DMs by subscribing.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/settings?tab=premium" className="px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright" data-testid="exclusive-upgrade-btn">
              Unlock with Premium →
            </Link>
            <Link to="/community" className="px-6 py-3 border border-white/20 text-white font-sub uppercase tracking-[0.25em] text-xs hover:border-pfc-gold hover:text-pfc-gold transition">
              Or join Community
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <div className="max-w-[1400px] mx-auto px-5 py-24 text-center text-white/60 font-sub uppercase tracking-[0.2em]" data-testid="profile-not-found">{error}</div>;
  if (!profile) return <div className="max-w-[1400px] mx-auto px-5 py-24 text-center text-white/40 font-sub uppercase tracking-[0.2em]">Loading…</div>;

  const isOwner = user && profile.owner_id && user.user_id === profile.owner_id;
  const heroSrc = profile.cover_image || "https://images.unsplash.com/photo-1517999144091-3d9dca6d1e43?w=1600";

  return (
    <div data-testid="profile-detail-page">
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img src={heroSrc} alt={profile.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
      </section>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 -mt-20 relative">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <Link to="/community" className="inline-flex items-center gap-2 font-sub uppercase tracking-[0.2em] text-xs text-pfc-gold"><ArrowLeft size={14} /> Back to Community</Link>
          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                onClick={() => setEditing(true)}
                className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition flex items-center gap-1.5"
                data-testid="profile-edit-btn"
              >
                <Pencil size={14} /> Edit Profile
              </button>
            )}
            {!isOwner && user && profile.owner_id && (
              <>
                <Link
                  to={`/messages/${profile.owner_id}`}
                  className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold transition flex items-center gap-1.5"
                  data-testid="profile-message-btn"
                >
                  <MessageCircle size={14} /> Message
                </Link>
                <Link
                  to="/community"
                  className="font-sub uppercase tracking-[0.25em] text-xs px-4 py-2.5 border border-pfc-gold/40 text-pfc-gold hover:bg-pfc-gold hover:text-ink-900 transition flex items-center gap-1.5"
                  data-testid="profile-connect-btn"
                >
                  <UserPlus size={14} /> Connect
                </Link>
              </>
            )}
          </div>
        </div>
        <span className="label bg-pfc-gold text-ink-900 px-3 py-1.5 inline-block mb-4">{profile.category}</span>
        {isOwner && profile.is_public === false && (
          <span className="ml-2 label border border-white/30 bg-ink-800 text-white/70 px-3 py-1.5 inline-block mb-4" data-testid="profile-private-badge">
            🔒 Private — only you can see this
          </span>
        )}
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">{profile.name}</h1>
        <p className="font-sub uppercase tracking-[0.2em] text-pfc-gold mt-4 text-sm sm:text-base">{profile.headline}</p>
        {profile.location && (
          <p className="flex items-center gap-2 text-white/60 text-sm mt-3 font-body"><MapPin size={14} /> {profile.location}</p>
        )}

        <div className="grid lg:grid-cols-3 gap-12 mt-16">
          <div className="lg:col-span-2 prose prose-invert max-w-none">
            <div className="label text-pfc-gold mb-4">Biography</div>
            <p className="text-white/80 text-lg leading-relaxed font-body whitespace-pre-line">{profile.bio || (isOwner ? "Tell the community about yourself — click Edit Profile above." : "—")}</p>

            {profile.gallery && profile.gallery.length > 0 && (
              <div className="mt-10" data-testid="profile-gallery">
                <div className="label text-pfc-gold mb-4">Gallery</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {profile.gallery.map((img) => (
                    <a key={img} href={img} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden pfc-card">
                      <img src={img} alt={profile.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {profile.spotify_artist_id && (
              <SpotifyEmbed artistId={profile.spotify_artist_id} title="Top tracks" />
            )}

            <StreamingLinks profile={profile} />

            {profile.youtube_video_id && (
              <YouTubeEmbed videoId={profile.youtube_video_id} title="Live performance" />
            )}

            {profile.collab_youtube_video_id && (
              <YouTubeEmbed videoId={profile.collab_youtube_video_id} title="Latest collab · Watch" />
            )}

            {profile.spotify_album_id && (
              <div className="mt-10 pfc-card p-6 lg:p-8" data-testid="profile-album-drop">
                <div className="label text-pfc-gold mb-2">💼 New Album — Drops Aug 11, 2026</div>
                <h3 className="font-display text-2xl lg:text-3xl text-white uppercase leading-tight mb-3">
                  Suit &amp; Tie <span className="gold-text">Vol. 1</span>
                </h3>
                <p className="text-white/70 font-body text-sm leading-relaxed mb-2">
                  A new chapter begins with powerful storytelling, elevated production, and a fresh sound that blends hustle, ambition, and real-life experiences.
                </p>
                <p className="text-white/60 font-body text-sm italic mb-4">Suit &amp; Tie Vol. 1 is more than an album — it&apos;s a statement.</p>
                <SpotifyEmbed albumId={profile.spotify_album_id} title="Stream the album" />
              </div>
            )}

            {profile.booking_policy && (
              <div className="mt-10 pfc-card p-6 lg:p-8" data-testid="profile-booking-policy">
                <div className="label text-pfc-gold mb-4">Booking Policy</div>
                <p className="text-white/85 leading-relaxed font-body whitespace-pre-line">{profile.booking_policy}</p>
              </div>
            )}

            {profile.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {profile.tags.map((t) => (
                  <span key={t} className="label border border-white/15 text-white/70 px-3 py-1.5">#{t}</span>
                ))}
              </div>
            )}

            <ProfileExtras profile={profile} />
          </div>
          <ProfileConnect profile={profile} />
        </div>
      </div>

      {editing && (
        <EditProfileModal
          profile={profile}
          onClose={() => setEditing(false)}
          onSaved={(p) => setProfile(p)}
        />
      )}
    </div>
  );
}
